import type { InspectionTemplate } from 'inspection-types';

import type { RefreshAttempt } from '../model/operations';
import { sortSections } from '../model/templateProjection';
import type { CustomizationState } from '../state/customizationState';
import { applyPendingReorders } from '../state/selectors';
import {
  createSectionsRailViewModel,
  type SectionsRailViewModel,
} from './createSectionsRailViewModel';
import {
  createSectionViewModel,
  type OperationInputs,
  type SectionViewModel,
} from './createSectionViewModel';
import {
  createTemplateHeaderViewModel,
  type TemplateHeaderViewModel,
} from './createTemplateHeaderViewModel';
import { memoizeByNode } from './memoizeByNode';
import { LABEL_KEYS, type LabelKey, type Message } from './messages';
import {
  resolveRecoveryBanner,
  type RecoveryBannerViewModel,
} from './resolveRecoveryBanner';

export type BreadcrumbViewModel = {
  key: LabelKey;
  isCurrent: boolean;
};

export type TemplateCustomizationViewModel = {
  breadcrumbs: BreadcrumbViewModel[];
  header: TemplateHeaderViewModel;
  rail: SectionsRailViewModel;
  sections: SectionViewModel[];
  emptyMessage: Message | null;
  recoveryBanner: RecoveryBannerViewModel | null;
};

export type TemplateCustomizationInput = OperationInputs & {
  template: InspectionTemplate;
  expandedBySectionId: CustomizationState['expandedBySectionId'];
  activeSectionId: CustomizationState['activeSectionId'];
  refreshAttempt: RefreshAttempt;
};

export const BREADCRUMBS: BreadcrumbViewModel[] = [
  { key: LABEL_KEYS.breadcrumbLibrary, isCurrent: false },
  { key: LABEL_KEYS.breadcrumbEditTemplate, isCurrent: true },
];

const createMemoizedSection = memoizeByNode(createSectionViewModel);

export function createTemplateCustomizationViewModel({
  template,
  expandedBySectionId,
  activeSectionId,
  refreshAttempt,
  ...operations
}: TemplateCustomizationInput): TemplateCustomizationViewModel {
  const sortedSections = sortSections(
    applyPendingReorders(template, operations).sections
  );
  const recoveryBanner = resolveRecoveryBanner({
    staleReason: operations.staleReason,
    refreshAttempt,
  });

  return {
    breadcrumbs: BREADCRUMBS,
    header: createTemplateHeaderViewModel(template),
    rail: createSectionsRailViewModel(sortedSections, activeSectionId),
    sections: sortedSections.map(section =>
      createMemoizedSection(
        section,
        expandedBySectionId[section.id] ?? false,
        operations
      )
    ),
    emptyMessage:
      sortedSections.length === 0 ? { key: LABEL_KEYS.noSections } : null,
    recoveryBanner,
  };
}
